import React from "react";
import { SmlJsonQueryFilter, SmlJsonQueryOrder } from "@src/types/intelligence-search";
import { css } from "@emotion/react";
import { useSmlQueryBuilderContext } from "@src/pages/intelligence-search-v2/context/sml-query-builder-context";
import { SearchTemplate, TSearchValue } from "@src/components/intelligence-search/search-template";
import { useManageSearchTemplate } from "@src/hooks/intelligence-search/use-manage-search-template";
import { useQueryDependenciesGraphContext } from "@src/pages/intelligence-search-v2/context/questions-context";
import dayjs from "dayjs";
import { convertFilterToPayload } from "@src/hooks/intelligence-search/use-fetch-next-options";
import {
    convertQueryFilterToSearchValues,
    convertSearchValuesToQueryFilter,
} from "@src/util/intelligence-search/transform";
import { config } from "@src/config";

const SmartSearch: React.FC<{
    filters: SmlJsonQueryFilter[];
    handleChange: ({
        selectedAssetsOrFilters,
        limit,
        orders,
    }: {
        selectedAssetsOrFilters?: SmlJsonQueryFilter[];
        orders?: SmlJsonQueryOrder[];
        limit?: number;
    }) => void;
    productCode?: string;
    platform?: string[];
    timeRange?: dayjs.Dayjs[];
    queryKey: string;
}> = ({ handleChange, filters, productCode, queryKey, platform, timeRange }) => {
    const { isPredecessor } = useQueryDependenciesGraphContext();
    const { getGeneratedJsonQuery } = useSmlQueryBuilderContext();
    const generatedJsonQuery = getGeneratedJsonQuery();

    const { values, setValues, options, optionsLoading, addNewOptionProps } = useManageSearchTemplate({
        startDate: timeRange?.[0]?.format(config.DATE_FORMAT),
        endDate: timeRange?.[1]?.format(config.DATE_FORMAT),
        productCode,
        isPreventFetchApi: false,
        labelProps: {
            transformValueToFilter: (values: TSearchValue[]) => {
                return convertSearchValuesToQueryFilter(values);
            },
            disabled(metaItem, currentFilterValues) {
                const unlikelyReasons = metaItem?.unlikely_reasons;
                const isHasUnlikelyReasons = Boolean(unlikelyReasons);
                const filterWithMeta = currentFilterValues.filter(
                    (f: SmlJsonQueryFilter) => f?.meta?.name === metaItem.name,
                );
                const disabledLabelOnly =
                    isHasUnlikelyReasons || filterWithMeta.some((f: SmlJsonQueryFilter) => !f.operator);
                const disabledFilterOnly =
                    isHasUnlikelyReasons || filterWithMeta.some((f: SmlJsonQueryFilter) => !!f.operator);

                return {
                    isCustomMetric: filterWithMeta.some((f: SmlJsonQueryFilter) => f.isCustomMetric),
                    disabledFull: isHasUnlikelyReasons,
                    disabledLabelOnly,
                    disabledFilterOnly,
                    hasDisabled: isHasUnlikelyReasons || disabledLabelOnly || disabledFilterOnly,
                    unlikelyReasons: unlikelyReasons,
                };
            },
        },

        smtJsonQueryPayload: generatedJsonQuery,
        addNewOptionProps: {
            handleFetchDryRun: (filters, dryRunCheck) => {
                return dryRunCheck(getGeneratedJsonQuery(convertFilterToPayload(filters, [])) ?? {});
            },
            currentFilterValues: filters,
            onSuccess(newFilterSelected) {
                handleChange({
                    selectedAssetsOrFilters: newFilterSelected,
                });
            },
        },
    });
    React.useEffect(() => {
        setValues(
            convertQueryFilterToSearchValues(filters, (item) => {
                return !isPredecessor(queryKey, item.name);
            }).filter((f) => f.meta),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(filters), isPredecessor, queryKey]);

    return (
        <div
            className="w-full"
            css={css`
                .ant-select-auto-complete {
                    width: 300px !important;
                }
            `}
            style={{
                backgroundColor: "rgba(233, 246, 254, 0.5)",
                borderColor: "rgba(24, 144, 255, 0.2)",
                borderStyle: "solid",
                borderWidth: "1px",
            }}
        >
            <SearchTemplate
                loading={optionsLoading}
                value={values}
                onChange={(values) => {
                    handleChange({
                        selectedAssetsOrFilters: convertSearchValuesToQueryFilter(values),
                    });
                }}
                options={options}
                searchModalFilterValueProps={{
                    productCode,
                    platform,
                    timeRange,
                }}
                addNewOptionProps={addNewOptionProps}
            />
        </div>
    );
};

export const SmartSearchMemo = React.memo(SmartSearch, (prev, next) => {
    const { handleChange: _1, ...restPrev } = prev;
    const { handleChange: _4, ...restNext } = next;
    return JSON.stringify(restPrev) === JSON.stringify(restNext);
});
